import { DateUtil, DynamicTextEditor, HtmlSanitizer, Input, DatePicker as TwsDatePicker, Modal as TwsModal } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import Constants from '../../../constants';
import ContentConstants from '../../../contentConstants';
import {
	DistributionType,
	EmailTemplatePropertiesUnderValidation,
	IAction,
	IAudience,
	IEmailTemplate,
	IValidationErrorCollection,
} from '../../../models';
import { ValidationErrorHelper } from '../../../utilities/ValidationErrorHelper';
import { Col3Col9, EnableHtml } from '../../common';
import * as AudienceValidation from '../AudienceValidation';
import { Timezone } from '../Timezone';
import { ToastType } from '../ToastType';
import { ReplacementTags } from './ReplacementTags';

const DatePicker = styled(TwsDatePicker)`
	& .rc-calendar-picker {
		z-index: 1051;
	}
`;
DatePicker.displayName = 'DatePicker';

const AddReminderEmailContainer = styled('div')`
	margin-top: 40px;
	margin-bottom: 40px;
	color: #696969;
	width: 100%;
	textarea {
		min-height: 250px;
	}
`;
const Modal = styled(TwsModal)`
	.modal-content {
		height: auto;
		min-height: 500px;
	}
	.modal-ref {
		height: auto;
		min-height: 500px;
	}
`;
Modal.displayName = 'Modal';

const datePickerFormat = 'MMM D, YYYY [at] h:mma';

export const NoteContainer = styled.div`
	height: 39px;
	width: 623px;
	color: #444444;
	font-family: Montserrat;
	font-size: 15px;
	font-weight: 500;
	line-height: 19px;
`;

type ReadOnlyProps = IReminderEmailModalProps & { children: any; save: () => void };

const ModalReadOnlyWrapper = (props: ReadOnlyProps) => {
	if (props.readOnly) {
		return (
			<Modal
				isOpen={props.modalOpen}
				title={ContentConstants.TITLE_REMINDER_EMAIL_READONLY}
				contenthcentered={true}
				contentvcentered={true}
				contentfullwidth={true}
				onClosed={props.closeModal}
				type={'lightbox'}
			>
				{props.children}
			</Modal>
		);
	} else {
		const modalTitle =
			props.reminderEmail.emailTemplateId === 0 ? ContentConstants.TITLE_REMINDER_EMAIL_ADD : ContentConstants.TITLE_REMINDER_EMAIL_EDIT;
		return (
			<Modal
				isOpen={props.modalOpen}
				title={modalTitle}
				primaryButtonText={
					props.reminderEmail.action === IAction.Insert ? ContentConstants.BUTTONS_SAVE_EMAIL : ContentConstants.BUTTONS_UPDATE_EMAIL
				}
				secondaryButtonText={'Cancel'}
				contenthcentered={true}
				contentvcentered={true}
				contentfullwidth={true}
				onClosed={props.closeModal}
				onSubmit={props.save}
			>
				{props.children}
			</Modal>
		);
	}
};

export interface IReminderEmailModalProps {
	audience: IAudience;
	reminderEmail: IEmailTemplate;
	modalOpen: boolean;
	readOnly?: boolean;
	validationErrors?: IValidationErrorCollection;
	reminderEmails?: IEmailTemplate[];
	closeModal: () => any;
	addReminderToList: (emailTemplate: IEmailTemplate) => any;
	addReminderEmailValidationErrors: (validationError?: IValidationErrorCollection) => any;
	toast: (type: ToastType, message: string) => any;
}

export class ReminderEmailModal extends React.Component<
	IReminderEmailModalProps,
	{
		reminderEmail: IEmailTemplate;
	}
> {
	constructor(props: IReminderEmailModalProps) {
		super(props);

		this.state = {
			reminderEmail: this.props.reminderEmail,
		};

		this.getDatePicker = this.getDatePicker.bind(this);
		this.onChange = this.onChange.bind(this);
		this.onDateChange = this.onDateChange.bind(this);
		this.save = this.save.bind(this);
		this.addPlaceholder = this.addPlaceholder.bind(this);
		this.onClearDate = this.onClearDate.bind(this);
		this.onEnableHtmlContentValueChange = this.onEnableHtmlContentValueChange.bind(this);
	}

	public UNSAFE_componentWillReceiveProps(nextProps: IReminderEmailModalProps) {
		if (!_.isEqual(nextProps.reminderEmail, this.props.reminderEmail)) {
			this.setState({ reminderEmail: nextProps.reminderEmail });
		}
	}

	public render() {
		const { readOnly, validationErrors, audience } = this.props;
		const { reminderEmail } = this.state;

		const validationErrorHelper = new ValidationErrorHelper<EmailTemplatePropertiesUnderValidation>(validationErrors);

		return (
			<>
				<ModalReadOnlyWrapper {...this.props} save={this.save}>
					<AddReminderEmailContainer className='tws-reminder-email-form'>
						{readOnly && (
							<>
								<NoteContainer>
									<b>{ContentConstants.SURVEY_ADMIN_INVITE_EMAIL_NOTE}</b>
									{ContentConstants.SURVEY_ADMIN_REMINDER_EMAIL_NOTE_CONTENT}
								</NoteContainer>
								<Col3Col9 />
							</>
						)}
						<Col3Col9 label={'Name of Reminder'}>
							<Input
								id='reminderEmailName'
								value={reminderEmail.emailTemplateName || ''}
								maxLength={50}
								error={!validationErrorHelper.IsValid(EmailTemplatePropertiesUnderValidation.Name)}
								message={validationErrorHelper.Errors(EmailTemplatePropertiesUnderValidation.Name)}
								onChange={(event: any) => this.onChange('emailTemplateName', event)}
								placeholder={ContentConstants.INPUT_PLACEHOLDER_REMINDER_EMAIL_NAME}
								disabled={readOnly}
								aria-label={'Name of Reminder'}
							/>
						</Col3Col9>
						<Col3Col9 label={'Send Date'}>
							{readOnly ? (
								<Input
									id='readOnlySendDate'
									value={DateUtil.formatDateToLocal(new Date(reminderEmail.scheduledFor ? reminderEmail.scheduledFor : ''))}
									placeholder={ContentConstants.INPUT_PLACEHOLDER_REMINDER_EMAIL_SENDDATE}
									disabled={readOnly}
									aria-label={'Send Date'}
								/>
							) : (
								this.getDatePicker(validationErrorHelper)
							)}
						</Col3Col9>
						<Col3Col9 label={ContentConstants.AUDIENCE_TIMEZONE_SECTION_LABEL}>
							<Timezone />
						</Col3Col9>
						<Col3Col9 label={'Subject Line'}>
							<Input
								id='reminderEmailSubject'
								value={reminderEmail.subject}
								maxLength={150}
								error={!validationErrorHelper.IsValid(EmailTemplatePropertiesUnderValidation.Subject)}
								message={validationErrorHelper.Errors(EmailTemplatePropertiesUnderValidation.Subject)}
								onChange={(event: any) => this.onChange('subject', event)}
								placeholder={ContentConstants.INPUT_PLACEHOLDER_REMINDER_EMAIL_SUBJECT}
								disabled={readOnly}
								aria-label={'Subject Line'}
							/>
						</Col3Col9>
						<Col3Col9 label={'Content'}>
							{!readOnly && (
								<>
									<p>{ContentConstants.INSTRUCTIONALTEXT_REMINDER_EMAIL_CONTENT}</p>
									{audience.distributionType === DistributionType.SSO && (
										<p>{ContentConstants.INSTRUCTIONALTEXT_INVITE_EMAIL_CONTENT_SSO}</p>
									)}
									<EnableHtml enableHtmlContent={reminderEmail.enableHtmlContent} getValue={this.onEnableHtmlContentValueChange} />
								</>
							)}
							<div style={{ display: 'flex' }}>
								<div style={{ width: '100%' }}>
									<DynamicTextEditor
										id='reminderEmailContentEditor'
										inputType='textarea'
										height='210px'
										placeholder={ContentConstants.INPUT_PLACEHOLDER_REMINDER_EMAIL_CONTENT}
										enableHtmlEditor={reminderEmail.enableHtmlContent}
										error={!validationErrorHelper.IsValid(EmailTemplatePropertiesUnderValidation.Content)}
										messages={validationErrorHelper.Errors(EmailTemplatePropertiesUnderValidation.Content)}
										readOnly={readOnly}
										value={reminderEmail.content}
										onChange={this.onEmailContentChange}
										htmlEditorMode={'custom'}
										toolbarConfiguration={Constants.customHtmlEditorOptions}
										aria-label={'Content'}
									/>
								</div>
								{!readOnly && (
									<ReplacementTags
										handler={this.addPlaceholder}
										showAccessCode={audience.distributionType === DistributionType.SingleLinkWithAccessCode}
									/>
								)}
							</div>
						</Col3Col9>
					</AddReminderEmailContainer>
				</ModalReadOnlyWrapper>
			</>
		);
	}

	public getDatePicker(validationErrorHelper: any) {
		const { audience } = this.props;
		const { reminderEmail } = this.state;

		const currentDate = moment().add(Constants.OPEN_ON_GAP, 'hour');
		currentDate.set({ minute: 0, second: 0, millisecond: 0 });
		let updatedOpenOn: string = audience.openOn || currentDate.format();
		if (moment(updatedOpenOn) < moment(currentDate.format())) {
			// if creating the reminder after the audience has been open, we want today's date to be the starting point
			updatedOpenOn = currentDate.format();
		}

		const closeOnWithGap: string = DateUtil.getUtcDateTime(
			moment(audience.closeOn).add(-Constants.REMINDER_SENDDATE_TO_CLOSEDATE_GAP, 'hour').toDate(),
		);

		return (
			<DatePicker
				aria-label={'Send Date'}
				value={reminderEmail.scheduledFor ? moment(reminderEmail.scheduledFor) : ''}
				textInputPlaceholder={ContentConstants.INPUT_PLACEHOLDER_REMINDER_EMAIL_SENDDATE}
				clearText={ContentConstants.DATEPICKER_CLEARTEXT}
				onChange={this.onDateChange}
				onClear={this.onClearDate}
				dateFormat={datePickerFormat}
				headerLabel='Send Date'
				enabledStartDate={moment(updatedOpenOn)}
				enabledEndDate={moment(closeOnWithGap)}
				error={!validationErrorHelper.IsValid(EmailTemplatePropertiesUnderValidation.ScheduledFor)}
				message={validationErrorHelper.Errors(EmailTemplatePropertiesUnderValidation.ScheduledFor)}
			/>
		);
	}

	public onDateChange(value: any) {
		const { reminderEmail } = this.state;
		this.setState({ reminderEmail: { ...reminderEmail, scheduledFor: value } });
	}

	public onChange = (key: string, event: any) => {
		const { reminderEmail } = this.state;
		const updatedReminderEmail: any = { ...reminderEmail };
		updatedReminderEmail[key] = event.target.value;
		this.setState({ reminderEmail: updatedReminderEmail });
	};

	public onEmailContentChange = (value: string) => {
		const { reminderEmail } = this.state;
		this.setState({ reminderEmail: { ...reminderEmail, content: value } });
	};

	public onClearDate = () => {
		const { reminderEmail } = this.state;
		this.setState({ reminderEmail: { ...reminderEmail, scheduledFor: undefined } });
	};

	private onEnableHtmlContentValueChange(value: boolean) {
		const { reminderEmail } = this.state;
		const content = new HtmlSanitizer().sanitizeText(reminderEmail.content);
		this.setState({ reminderEmail: { ...reminderEmail, enableHtmlContent: value, content } });
	}

	private addPlaceholder(placeholder: string) {
		const { reminderEmail } = this.state;

		this.setState({ reminderEmail: { ...reminderEmail, content: reminderEmail.content + `\r\n${placeholder}` } });
	}

	private save = () => {
		const { addReminderToList, closeModal, toast, reminderEmails, audience } = this.props;
		const { reminderEmail } = this.state;
		if (addReminderToList) {
			let validationErrors = AudienceValidation.validEmailTemplateFields(
				[
					EmailTemplatePropertiesUnderValidation.Name,
					EmailTemplatePropertiesUnderValidation.ScheduledFor,
					EmailTemplatePropertiesUnderValidation.Subject,
					EmailTemplatePropertiesUnderValidation.Content,
					EmailTemplatePropertiesUnderValidation.SurveyLink,
				],
				reminderEmail,
				audience.distributionType,
			);

			if (reminderEmail.emailTemplateName && reminderEmails) {
				const duplicateReminderNameValidationError = AudienceValidation.validateReminderNameForDuplicity(reminderEmails, reminderEmail);
				const emailTemplateNameProperty = EmailTemplatePropertiesUnderValidation.Name;

				if (duplicateReminderNameValidationError) {
					if (validationErrors != null) {
						const nameAudienceValidation = validationErrors[emailTemplateNameProperty];
						if (nameAudienceValidation != null && nameAudienceValidation.length > 0) {
							validationErrors[emailTemplateNameProperty].push(duplicateReminderNameValidationError[emailTemplateNameProperty][0]);
						} else {
							validationErrors[emailTemplateNameProperty] = duplicateReminderNameValidationError[emailTemplateNameProperty];
						}
					} else {
						validationErrors = duplicateReminderNameValidationError;
					}
				}
			}

			this.props.addReminderEmailValidationErrors(validationErrors);
			if (!validationErrors) {
				addReminderToList(reminderEmail);
				toast(ToastType.Success, 'Email Reminder Added');
				closeModal();
			} else {
				if (toast) {
					toast(ToastType.Warning, ContentConstants.TOAST_ERROR_GENERIC_BODY);
				}
			}
		}
	};
}
